/** Where validation's issues show: under their field in the form, and in a summary above both views. */
import { AlertCircle, AlertTriangle, Info, Loader2 } from "lucide-react";
import { createContext, useContext, type ReactNode } from "react";
import { useT } from "../../i18n";
import type { Path } from "./specDocument";
import type { Issue, Validation, VariableUse } from "./useValidation";

const IssuesContext = createContext<Validation | null>(null);

export function IssuesProvider({ validation, children }: { validation: Validation; children: ReactNode }) {
  return <IssuesContext.Provider value={validation}>{children}</IssuesContext.Provider>;
}

export function useIssuesAt(path: Path): Issue[] {
  return useContext(IssuesContext)?.at(path) ?? [];
}

/** Every {{scope.name}} the agent uses, from the last check (V1.1-EA-T05). */
export function useVariableUses(): VariableUse[] {
  return useContext(IssuesContext)?.variables ?? [];
}

const ICON = { error: AlertCircle, warning: AlertTriangle, info: Info } as const;
const COLOR = { error: "text-[var(--color-danger)]", warning: "text-[var(--color-warning)]", info: "text-[var(--color-muted)]" } as const;

/** The issues of one field, under it; `id` is what the field's aria-describedby points at. */
export function FieldIssues({ id, issues }: { id: string; issues: Issue[] }) {
  if (!issues.length) return null;
  return (
    <ul id={id} className="space-y-0.5">
      {issues.map((issue, i) => {
        const Icon = ICON[issue.severity];
        return (
          <li key={i} className={`flex items-start gap-1.5 text-xs ${COLOR[issue.severity]}`}>
            <Icon size={14} aria-hidden="true" className="mt-px shrink-0" />
            <span>{issue.message}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function IssueSummary({ validation, onShow }: { validation: Validation; onShow: (issue: Issue) => void }) {
  const t = useT();
  const problems = validation.issues.filter((i) => i.severity !== "info");
  const errors = problems.filter((i) => i.severity === "error").length;
  return (
    <section aria-label={t("builder.check")} className="rounded-lg border border-[var(--color-border)] p-3 text-sm">
      <p role="status" aria-live="polite" className="flex items-center gap-2 font-medium">
        {validation.checking ? (
          <>
            <Loader2 size={14} aria-hidden="true" className="animate-spin" />
            {t("builder.checking")}
          </>
        ) : problems.length === 0 ? (
          t("builder.allGood")
        ) : (
          t("builder.issueCount", { errors, warnings: problems.length - errors })
        )}
      </p>
      {problems.length > 0 && (
        <ul className="mt-2 space-y-1">
          {problems.map((issue, i) => {
            const Icon = ICON[issue.severity];
            return (
              <li key={i} className="flex items-start gap-2">
                <Icon size={14} aria-hidden="true" className={`mt-0.5 shrink-0 ${COLOR[issue.severity]}`} />
                <span className="flex-1">
                  <code className="text-xs">{issue.path}</code> {issue.message}
                </span>
                <button type="button" onClick={() => onShow(issue)} className="text-[var(--color-primary)] underline">
                  {t("builder.show")}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
